import {useCallback, useEffect, useRef, useState} from "react";
import {ABORT_ERROR, SEARCH_DEBOUNCE_DELAY} from "src/constants/appConstants";

interface UseDebouncedFetchOptions<T> {
    /** Async function that performs the fetch. Receives AbortSignal for cancellation. */
    fetchFn: (signal: AbortSignal) => Promise<T | null>;
    /** Debounce delay in milliseconds. Defaults to SEARCH_DEBOUNCE_DELAY. */
    delay?: number;
    /** Whether the fetch should execute. Defaults to true. */
    enabled?: boolean;
    /** Additional dependencies that trigger re-fetch when changed. */
    deps?: ReadonlyArray<unknown>;
    /** Callback invoked on successful fetch. */
    onSuccess?: (data: T) => void;
    /** Callback invoked on fetch error. */
    onError?: (error: Error) => void;
}

interface UseDebouncedFetchResult<T> {
    /** The fetched data, or null if not yet fetched or fetch failed. */
    data: T | null;
    /** Whether a fetch is currently in progress. */
    isLoading: boolean;
    /** The error from the last fetch attempt, or null if successful. */
    error: Error | null;
    /** Function to manually trigger a re-fetch. */
    refetch: () => void;
}

/**
 * A reusable hook for debounced async fetching with AbortController support.
 * Automatically cancels previous requests when dependencies change.
 *
 * @example
 * const {data, isLoading} = useDebouncedFetch({
 *   fetchFn: (signal) => searchIngredients([searchValue], signal),
 *   enabled: searchValue.length > 2,
 *   deps: [searchValue],
 *   onSuccess: (results) => setSuggestions(results.flexibleMatchResults),
 * });
 */
export function useDebouncedFetch<T>({
    fetchFn,
    delay = SEARCH_DEBOUNCE_DELAY,
    enabled = true,
    deps = [],
    onSuccess,
    onError,
}: UseDebouncedFetchOptions<T>): UseDebouncedFetchResult<T> {
    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<Error | null>(null);
    const [trigger, setTrigger] = useState(0);

    // Use refs to avoid stale closures while keeping stable effect dependencies
    const fetchFnRef = useRef(fetchFn);
    const onSuccessRef = useRef(onSuccess);
    const onErrorRef = useRef(onError);

    // Update refs on each render
    fetchFnRef.current = fetchFn;
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;

    const refetch = useCallback(() => setTrigger((t) => t + 1), []);

    useEffect(() => {
        if (!enabled) {
            setData(null);
            setIsLoading(false);
            return;
        }

        const controller = new AbortController();

        const execute = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const result = await fetchFnRef.current(controller.signal);

                // Only update state if request wasn't aborted
                if (!controller.signal.aborted && result !== null) {
                    setData(result);
                    onSuccessRef.current?.(result);
                }
            } catch (err) {
                // Silently ignore abort errors - they're expected during cleanup
                if (err instanceof Error && err.name === ABORT_ERROR) {
                    return;
                }

                const fetchError = err instanceof Error ? err : new Error(String(err));

                if (!controller.signal.aborted) {
                    setError(fetchError);
                    onErrorRef.current?.(fetchError);
                    console.error("Error in useDebouncedFetch:", fetchError);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            }
        };

        const timeoutId = setTimeout(execute, delay);

        // Cleanup: cancel timeout and abort in-flight request
        return () => {
            clearTimeout(timeoutId);
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, delay, trigger, ...deps]);

    return {data, isLoading, error, refetch};
}
